/**
 * LocationSummary
 * Orchestrator for the ASN Gap "Location Summary" tab —
 * `POST /api/asn/location-details`.
 *
 * Chart Configuration (grain/column selection), perspective, sort, search,
 * and pagination are all Redux-backed via `asnLocationSummarySlice`,
 * following the exact same pattern `KPIOnePagerV2Tab` uses with
 * `onePagerSlice` + `ChartConfigurationPanel`: a committed `grainSelections`
 * array drives the table, plus a separate `pendingGrains` draft (edited in
 * the panel, only committed on Apply). `AsnChartConfigurationPanel` (the
 * ASN equivalent of `ChartConfigurationPanel`) is wired directly to Redux
 * state/dispatch here, the same way `KPIOnePagerV2Tab` wires
 * `ChartConfigurationPanel` directly to `onePagerSlice`.
 *
 * This component owns the request lifecycle: it builds the
 * `/api/asn/location-details` payload (shared filter fields from
 * `filterInputs`, owned by `AsnGapDashboard` — reused, never duplicated —
 * plus this tab's own perspective/grainSelections/sort/search/pagination),
 * checks the Redux response cache before calling the API, and dispatches
 * loading/data/error into `asnLocationSummarySlice`. The table components
 * (`AsnLocationDetailsTable` / `AsnLocationDetailsMobileView`) read
 * rows/totals/pagination/loading/error directly from that same Redux slice
 * — no response data is copied into component state anywhere.
 *
 * The API is called: on first activation of this tab, when the shared
 * filter bar's Apply/Reset is clicked (via the `applyVersion` counter
 * bumped by `AsnGapDashboard`), when the Chart Configuration grain columns
 * are applied/reset, when perspective is toggled, when search is
 * submitted (debounced), and when sorting/pagination change. `pageNumber`
 * resets to 0 on every trigger except a plain page change.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, StyleSheet, TouchableOpacity, TextInput, useWindowDimensions, Text, ScrollView, Platform } from 'react-native';
import { useSelector } from 'react-redux';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '../../../../../theme/useTheme';
import { FontFamily } from '../../../../../constants/fonts';
import { RootState, useAppDispatch } from '../../../../../store';
import { fetchAsnLocationDetails, AsnLocationGrainKey } from '../api/asnOverviewKpiApi';
import {
  setAsnLocationSummaryPerspective,
  setAsnLocationSummarySort,
  setAsnLocationSummarySearchQuery,
  setAsnLocationSummaryPageNumber,
  setAsnLocationSummaryPageSize,
  setAsnLocationSummaryLoading,
  setAsnLocationSummaryData,
  setAsnLocationSummaryError,
  setAsnLocationSummaryHasLoadedOnce,
  setAsnLocationSummaryLastRequest,
  buildAsnLocationSummaryCacheKey,
  AsnLocationSummaryPerspective,
} from '../../../../../store/slices/asnLocationSummarySlice';
import { ASN_METRIC_COLUMNS, getAsnGrainLabel, AsnMetricColumn } from '../constants/asnOnePagerConstants';
import {
  buildAsnLocationDetailsRequest,
  AsnOverviewKpiRequestInputs,
} from '../utils/asnOverviewKpiRequestBuilder';
import AsnLocationDetailsTable from './AsnLocationDetailsTable';
import AsnLocationDetailsMobileView from './AsnLocationDetailsMobileView';
import { useLocationSummaryExport } from '../hooks/useLocationSummaryExport';

export type AsnGapPerspective = AsnLocationSummaryPerspective;

const MOBILE_BREAKPOINT = 768;
const SEARCH_DEBOUNCE_MS = 350;

// ─── Column picker helpers ───────────────────────────────────────────────────

type ColumnPickerId = string;

interface ColumnPickerItem {
  id: ColumnPickerId;
  label: string;
  group: 'grain' | 'metric';
}

function buildColumnPickerItems(effectiveGrainKeys: AsnLocationGrainKey[]): ColumnPickerItem[] {
  const grainItems: ColumnPickerItem[] = effectiveGrainKeys.map((k) => ({
    id: k,
    label: getAsnGrainLabel(k),
    group: 'grain',
  }));
  const metricItems: ColumnPickerItem[] = ASN_METRIC_COLUMNS.map((col) => ({
    id: col.id,
    label: col.label,
    group: 'metric',
  }));
  return [...grainItems, ...metricItems];
}

function buildDefaultVisibility(items: ColumnPickerItem[]): Record<ColumnPickerId, boolean> {
  return items.reduce((acc, item) => ({ ...acc, [item.id]: true }), {} as Record<ColumnPickerId, boolean>);
}

export interface LocationSummaryProps {
  /** Shared ASN Gap filter bar selections, owned by AsnGapDashboard and
   * reused as-is here — never duplicated into local/Redux state. Always
   * reflects the current (not-yet-applied) filter dropdown values; this
   * component only reads it at the moment it actually fires a request
   * (mount, grain apply/reset, perspective/sort/search/page changes), and
   * relies on `applyVersion` (bumped by AsnGapDashboard) to know when the
   * shared Apply/Reset button itself should trigger a refetch. */
  readonly filterInputs: AsnOverviewKpiRequestInputs;
  /** True once the time filter dropdown has been fully pre-populated with its
   * initial/default values (e.g. the current period has been resolved from
   * the backend). The initial API call is deferred until this is true so
   * the request is never fired with an empty period filter. Defaults to true
   * so omitting this prop preserves backward-compatible behaviour. */
  readonly isFilterReady?: boolean;
}

interface RunFetchOverrides {
  perspective?: AsnLocationSummaryPerspective;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  searchQuery?: string;
  pageNumber?: number;
  pageSize?: number;
}

const LocationSummary: React.FC<LocationSummaryProps> = ({ filterInputs = {} as any, isFilterReady = true }) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = windowWidth < MOBILE_BREAKPOINT;
  const dispatch = useAppDispatch();

  // ─── Redux-backed state ───────────────────────────────────────────────
  const grainSelections = useSelector((state: RootState) => state.asnLocationSummary.grainSelections);
  const perspective = useSelector((state: RootState) => state.asnLocationSummary.perspective);
  const sortBy = useSelector((state: RootState) => state.asnLocationSummary.sortBy);
  const sortDirection = useSelector((state: RootState) => state.asnLocationSummary.sortDirection);
  const searchQuery = useSelector((state: RootState) => state.asnLocationSummary.searchQuery);
  const pageNumber = useSelector((state: RootState) => state.asnLocationSummary.pageNumber);
  const pageSize = useSelector((state: RootState) => state.asnLocationSummary.pageSize);
  const hasLoadedOnce = useSelector((state: RootState) => state.asnLocationSummary.hasLoadedOnce);
  const applyVersion = useSelector((state: RootState) => state.asnLocationSummary.applyVersion);
  const cache = useSelector((state: RootState) => state.asnLocationSummary.cache);
  const lastRequest = useSelector((state: RootState) => state.asnLocationSummary.lastRequest);
  const data = useSelector((state: RootState) => state.asnLocationSummary.data);

  const effectiveGrainKeys: AsnLocationGrainKey[] =
    data?.effectiveGrainKeys?.length ? data.effectiveGrainKeys : grainSelections;

  // ─── Column picker state ──────────────────────────────────────────────
  const [isColumnPickerOpen, setIsColumnPickerOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left?: number;
    right?: number;
    width: number;
  } | null>(null);
  const cardRef = useRef<any>(null);
  const columnsAnchorRef = useRef<any>(null);
  const columnsPopoverRef = useRef<any>(null);

  const [columnPickerItems, setColumnPickerItems] = useState<ColumnPickerItem[]>(() =>
    buildColumnPickerItems(effectiveGrainKeys),
  );
  const [visibleColumns, setVisibleColumns] = useState<Record<ColumnPickerId, boolean>>(() =>
    buildDefaultVisibility(buildColumnPickerItems(effectiveGrainKeys)),
  );

  // Rebuild picker when effective grain keys change after a fetch
  useEffect(() => {
    const next = buildColumnPickerItems(effectiveGrainKeys);
    setColumnPickerItems(next);
    setVisibleColumns((prevVis) => {
      const updated: Record<ColumnPickerId, boolean> = {};
      for (const item of next) {
        updated[item.id] = item.id in prevVis ? prevVis[item.id] : true;
      }
      return updated;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveGrainKeys.join(',')]);

  const visibleGrainKeys: Set<string> | undefined = React.useMemo(() => {
    const grainItems = columnPickerItems.filter((i) => i.group === 'grain');
    const allVisible = grainItems.every((i) => visibleColumns[i.id]);
    if (allVisible) return undefined;
    return new Set(grainItems.filter((i) => visibleColumns[i.id]).map((i) => i.id));
  }, [columnPickerItems, visibleColumns]);

  const visibleMetricColumnIds: Set<string> | undefined = React.useMemo(() => {
    const metricItems = columnPickerItems.filter((i) => i.group === 'metric');
    const allVisible = metricItems.every((i) => visibleColumns[i.id]);
    if (allVisible) return undefined;
    return new Set(metricItems.filter((i) => visibleColumns[i.id]).map((i) => i.id));
  }, [columnPickerItems, visibleColumns]);

  // Export always includes every metric column regardless of column-picker
  // visibility — the exported document shows the full dataset, not only what
  // is currently visible on screen.
  const allMetricColumnsForExport: AsnMetricColumn[] = ASN_METRIC_COLUMNS;

  const toggleColumn = (id: ColumnPickerId) => {
    setVisibleColumns((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleToggleColumnPicker = useCallback(() => {
    if (isColumnPickerOpen) {
      setIsColumnPickerOpen(false);
      return;
    }
    const anchorNode = columnsAnchorRef.current;
    const cardNode = cardRef.current;
    const dropdownWidth = Math.min(240, windowWidth - 32);
    if (anchorNode?.measureInWindow && cardNode?.measureInWindow) {
      anchorNode.measureInWindow((ax: number, ay: number, aw: number, ah: number) => {
        cardNode.measureInWindow((cx: number, cy: number, cw: number) => {
          const top = ay - cy + ah + 4;
          const fitsRight = ax - cx + dropdownWidth <= cw - 8;
          setDropdownPos(
            fitsRight
              ? { top, left: ax - cx, width: dropdownWidth }
              : { top, right: cx + cw - (ax + aw), width: dropdownWidth },
          );
          setIsColumnPickerOpen(true);
        });
      });
    } else {
      setDropdownPos({ top: 44, left: 0, width: dropdownWidth });
      setIsColumnPickerOpen(true);
    }
  }, [isColumnPickerOpen, windowWidth]);

  useEffect(() => {
    if (!isColumnPickerOpen || Platform.OS !== 'web' || typeof document === 'undefined') return;
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      const anchorNode = columnsAnchorRef.current;
      const popoverNode = columnsPopoverRef.current;
      if (
        target &&
        ((anchorNode?.contains && anchorNode.contains(target)) ||
          (popoverNode?.contains && popoverNode.contains(target)))
      ) {
        return;
      }
      setIsColumnPickerOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsColumnPickerOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isColumnPickerOpen]);

  useEffect(() => {
    if (!isColumnPickerOpen) return;
    setIsColumnPickerOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowWidth]);

  // ─── Export ───────────────────────────────────────────────────────────
  const { isExporting, exportLocationSummary } = useLocationSummaryExport();

  const handleExport = useCallback(() => {
    if (!lastRequest) return;
    // Always export with all grain keys returned by the API and all metric
    // columns — column-picker visibility only affects the on-screen table,
    // never the exported document.
    const allGrainKeys: AsnLocationGrainKey[] =
      data?.effectiveGrainKeys?.length ? data.effectiveGrainKeys : grainSelections;
    exportLocationSummary(lastRequest, allGrainKeys, allMetricColumnsForExport);
  }, [lastRequest, data?.effectiveGrainKeys, grainSelections, allMetricColumnsForExport, exportLocationSummary]);

  // ─── Search box ───────────────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState(searchQuery);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);
  useEffect(
    () => () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    },
    [],
  );

  // Guards against an older, slower request overwriting a newer response —
  // only the response matching the latest fired request id is applied.
  const requestIdRef = useRef(0);

  // ─── Core fetch (checks Redux cache before calling the API) ──────────
  const runFetch = useCallback(
    (overrides?: RunFetchOverrides) => {
      const request = buildAsnLocationDetailsRequest({
        ...filterInputs,
        perspective: overrides?.perspective ?? perspective,
        grainSelections,
        sortBy: overrides?.sortBy ?? sortBy,
        sortDirection: (overrides?.sortDirection ?? sortDirection).toUpperCase() as 'ASC' | 'DESC',
        searchQuery: overrides?.searchQuery ?? searchQuery,
        pageNumber: overrides?.pageNumber ?? pageNumber,
        pageSize: overrides?.pageSize ?? pageSize,
      });

      dispatch(setAsnLocationSummaryLastRequest(request));

      const requestKey = buildAsnLocationSummaryCacheKey(request);
      const cached = cache[requestKey];
      if (cached) {
        // Exact request already in Redux — reuse it instead of calling the API again.
        dispatch(setAsnLocationSummaryData({ response: cached, requestKey }));
        return;
      }

      const requestId = ++requestIdRef.current;
      dispatch(setAsnLocationSummaryLoading());
      fetchAsnLocationDetails(request)
        .then((response) => {
          if (requestId !== requestIdRef.current) return; // stale response — ignore
          dispatch(setAsnLocationSummaryData({ response, requestKey }));
        })
        .catch((err: any) => {
          if (requestId !== requestIdRef.current) return; // stale response — ignore
          const msg =
            err?.response?.data?.message ||
            err?.message ||
            'An unexpected error occurred while loading the ASN Gap Location Summary.';
          dispatch(setAsnLocationSummaryError(msg));
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, filterInputs, perspective, grainSelections, sortBy, sortDirection, searchQuery, pageNumber, pageSize, cache],
  );

  // ─── Trigger 1: initial load (first activation of this tab) ──────────
  // Deferred until isFilterReady is true so the request is never fired
  // with an empty period filter (before currentPeriodWeek has resolved).
  useEffect(() => {
    if (!isFilterReady) return;
    if (hasLoadedOnce) return;
    dispatch(setAsnLocationSummaryHasLoadedOnce());
    runFetch({ pageNumber: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilterReady]);

  // ─── Trigger 2/3: shared filter bar Apply/Reset (bumped by AsnGapDashboard) ──
  const isFirstApplyVersionRun = useRef(true);
  useEffect(() => {
    if (isFirstApplyVersionRun.current) {
      isFirstApplyVersionRun.current = false;
      return;
    }
    runFetch({ pageNumber: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyVersion]);

  // ─── Trigger 3: perspective toggle ─────────────────────────────────────
  const handlePerspectiveChange = useCallback(
    (nextPerspective: AsnLocationSummaryPerspective) => {
      if (nextPerspective === perspective) return;
      dispatch(setAsnLocationSummaryPerspective(nextPerspective));
      runFetch({ perspective: nextPerspective, pageNumber: 0 });
    },
    [dispatch, perspective, runFetch],
  );

  // ─── Trigger 4: sorting ───────────────────────────────────────────────
  const handleSort = useCallback(
    (col: string) => {
      const nextDir: 'asc' | 'desc' = sortBy === col && sortDirection === 'asc' ? 'desc' : 'asc';
      dispatch(setAsnLocationSummarySort({ sortBy: col, sortDirection: nextDir }));
      runFetch({ sortBy: col, sortDirection: nextDir, pageNumber: 0 });
    },
    [dispatch, sortBy, sortDirection, runFetch],
  );

  // ─── Trigger 5: pagination ────────────────────────────────────────────
  const handlePageChange = useCallback(
    (nextPageNumber: number) => {
      dispatch(setAsnLocationSummaryPageNumber(nextPageNumber));
      runFetch({ pageNumber: nextPageNumber });
    },
    [dispatch, runFetch],
  );

  const handlePageSizeChange = useCallback(
    (nextPageSize: number) => {
      dispatch(setAsnLocationSummaryPageSize(nextPageSize));
      runFetch({ pageSize: nextPageSize, pageNumber: 0 });
    },
    [dispatch, runFetch],
  );

  // ─── Trigger 6: search (debounced) ────────────────────────────────────
  const handleSearchInputChange = useCallback(
    (text: string) => {
      setSearchInput(text);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        dispatch(setAsnLocationSummarySearchQuery(text));
        runFetch({ searchQuery: text, pageNumber: 0 });
      }, SEARCH_DEBOUNCE_MS);
    },
    [dispatch, runFetch],
  );

  return (
    <View style={styles.container} ref={cardRef}>
      <View style={styles.controlBar}>
        <Text style={styles.label}>Perspective:</Text>
        <View style={styles.perspectiveControl}>
          {(['shipping', 'receiving'] as const).map((p) => {
            const isActive = perspective === p;
            return (
              <TouchableOpacity
                key={p}
                style={[
                  styles.perspectiveOption,
                  !isActive && isDark && { backgroundColor: '#2B2B2B', borderWidth: 1, borderColor: '#4A4A4A' },
                  isActive && styles.perspectiveOptionActive,
                ]}
                onPress={() => handlePerspectiveChange(p)}
                testID={p === 'receiving' ? 'asn-gap-perspective-receiving' : 'asn-gap-perspective-shipping'}
              >
                <Text
                  style={[
                    styles.perspectiveText,
                    !isActive && isDark && { color: '#D1D5DB' },
                    isActive && styles.perspectiveTextActive,
                  ]}
                >
                  {p === 'shipping' ? 'Shipping' : 'Receiving'}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>

      </View>

      {!isMobile ? (
        <AsnLocationDetailsTable
          onSort={handleSort}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          visibleGrainKeys={visibleGrainKeys}
          visibleMetricColumnIds={visibleMetricColumnIds}
          onExport={handleExport}
          isExporting={isExporting}
          searchValue={searchInput}
          onSearchChange={handleSearchInputChange}
          columnsButtonRef={columnsAnchorRef}
          onColumnsPress={handleToggleColumnPicker}
          perspective={perspective}
        />
      ) : (
        <AsnLocationDetailsMobileView />
      )}

      {isColumnPickerOpen && dropdownPos && (
        <>
          <TouchableOpacity
            style={[styles.columnScrim, { top: dropdownPos.top }]}
            activeOpacity={1}
            onPress={() => setIsColumnPickerOpen(false)}
            testID="asn-gap-location-summary-columns-scrim"
          />
          <View
            ref={columnsPopoverRef}
            style={[
              styles.columnPopover,
              {
                top: dropdownPos.top,
                width: dropdownPos.width,
                ...(dropdownPos.left !== undefined
                  ? { left: dropdownPos.left }
                  : { right: dropdownPos.right }),
              },
            ]}
            testID="asn-gap-location-summary-columns-popover"
          >
            <ScrollView style={styles.columnPopoverList}>
              {columnPickerItems.map((item) => (
                <TouchableOpacity
                  key={item.id}
                  style={styles.columnPopoverItem}
                  onPress={() => toggleColumn(item.id)}
                  testID={`asn-gap-location-summary-column-${item.id}`}
                >
                  <MaterialIcons
                    name={visibleColumns[item.id] ? 'check-box' : 'check-box-outline-blank'}
                    size={18}
                    color={visibleColumns[item.id] ? '#005CBC' : '#9AA1AB'}
                  />
                  <Text style={styles.columnPopoverItemText}>{item.label}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, gap: 12 },
  controlBar: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E3E9F3',
    paddingHorizontal: 16,
    paddingVertical: 12,
    shadowColor: '#A8B4C5',
    shadowOpacity: 0.08,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  label: { fontSize: 13, fontFamily: FontFamily.bold, fontWeight: '700', color: '#6A6D71' },
  perspectiveControl: { flexDirection: 'row', backgroundColor: '#EFF5FC', borderRadius: 6, padding: 3, gap: 2 },
  perspectiveOption: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 4 },
  perspectiveOptionActive: { backgroundColor: '#0B5FC6' },
  perspectiveText: { fontSize: 12, fontFamily: FontFamily.medium, fontWeight: '600', color: '#495057' },
  perspectiveTextActive: { color: '#FFFFFF', fontFamily: FontFamily.bold },
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
    maxWidth: 320,
    backgroundColor: '#F5F7FA',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E3E9F3',
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  searchInput: { flex: 1, fontSize: 13, color: '#2F2F2F', paddingVertical: 0 },
  columnScrim: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.15)',
    zIndex: 15,
  },
  columnPopover: {
    position: 'absolute',
    maxHeight: 320,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D0D0D0',
    borderRadius: 8,
    paddingVertical: 6,
    shadowColor: '#3A3A3A',
    shadowOpacity: 0.12,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 },
    elevation: 6,
    zIndex: 20,
  },
  columnPopoverList: {
    maxHeight: 320,
  },
  columnPopoverItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 6,
  },
  columnPopoverItemText: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: '#1A1D21',
    flexShrink: 1,
  },
});

export default LocationSummary;
